import { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { signOut } from '../lib/auth';
import { isNative } from '../lib/capacitor';
import { useMe } from '../lib/me-context';
import { unregisterPushNotifications } from '../lib/push';
import { FeedbackModal } from './FeedbackModal';
import { NativeTabBar } from './NativeTabBar';
import { ThemeToggle } from './ThemeToggle';

async function handleSignOut() {
  // Best-effort: drop the device's FCM token client-side AND server-side
  // before the session goes. If the phone is shared, the next user
  // wouldn't keep receiving the previous account's match pings. Failures
  // are swallowed — sign-out still proceeds.
  try {
    await Promise.allSettled([
      unregisterPushNotifications(),
      fetch('/api/devices', { method: 'DELETE', credentials: 'include' }),
    ]);
  } catch {
    // ignore
  }
  await signOut();
  window.location.reload();
}

/** Inline rail glyphs — stroked, 18px, currentColor (see .rlink svg). */
function RailIcon({ name }: { name: 'home' | 'chat' | 'shield' | 'spark' | 'gear' }) {
  const paths = {
    home: (
      <>
        <path d="M3 10.5 12 3l9 7.5" />
        <path d="M5 9.5V20h14V9.5" />
      </>
    ),
    chat: <path d="M4 5h16v11H9l-4 3v-3H4z" />,
    shield: <path d="M12 3l7 3v6c0 4-3 6.5-7 9-4-2.5-7-5-7-9V6z" />,
    spark: (
      <>
        <path d="M12 3v6M12 15v6M3 12h6M15 12h6" />
      </>
    ),
    gear: (
      <>
        <circle cx="12" cy="12" r="3.2" />
        <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.5 5.5l2 2M16.5 16.5l2 2M18.5 5.5l-2 2M7.5 16.5l-2 2" />
      </>
    ),
  }[name];
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      {paths}
    </svg>
  );
}

/**
 * The signed-in app shell. Three flavours:
 *
 *   - Native (Capacitor): a slim wordmark header pinned to the safe-area
 *     top, the routed page, and a bottom tab bar. Feels like a real app.
 *
 *   - Web desktop (≥ 60rem): a persistent left rail — wordmark, primary
 *     nav, and the 988 safety note + sign-out anchored at the bottom — beside
 *     a wide content area. The app finally has a *frame* on a big screen
 *     instead of a phone-width column floating in a void.
 *
 *   - Web mobile (< 60rem): mirrors the app — a slim wordmark appbar, the
 *     same bottom tab bar the Capacitor shell uses, and the quiet legal
 *     footer. The rail collapses away (CSS); sign-out lives on Settings.
 *
 * isNative() reads at every render so a Capacitor runtime that loaded a
 * tick late doesn't lock us into the web shell.
 */
export function AppLayout() {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const native = isNative();
  const me = useMe();
  const { pathname } = useLocation();

  if (native) {
    return (
      <>
        <header className="native-appbar" role="banner">
          <Link to="/" className="native-appbar-wordmark">
            <em className="wordmark-accent">Less</em>Alone
          </Link>
        </header>
        <div className="native-page">
          <Outlet />
        </div>
        <NativeTabBar />
        {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} />}
      </>
    );
  }

  const isHome = pathname === '/' || pathname.startsWith('/conversations');
  const isReflections = pathname.startsWith('/reflections');
  const isSettings = pathname.startsWith('/settings');
  const isModeration = pathname.startsWith('/moderation');
  const isFounder = pathname.startsWith('/founder');

  // The active community's name, to give the rail the same "Community"
  // context the desktop concept shows. Reads the same persisted key Home
  // uses; falls back to the first membership. (Display only — switching
  // still happens from the dashboard header.)
  const memberships = me?.memberships ?? [];
  const activeCommunityName = (() => {
    const first = memberships[0];
    if (!first) return null;
    let id = first.communityId;
    try {
      const stored = window.localStorage.getItem('la-active-community');
      if (stored && memberships.some((m) => m.communityId === stored)) id = stored;
    } catch {
      // ignore
    }
    return memberships.find((m) => m.communityId === id)?.communityName ?? first.communityName;
  })();

  return (
    <div className="app-shell">
      <aside className="app-rail" aria-label="Primary navigation">
        <Link to="/" className="rail-word">
          <span className="wordmark-accent">Less</span>Alone
        </Link>
        {activeCommunityName && (
          <div className="rail-comm">
            <span className="rail-comm-label">Community</span>
            <span className="rail-comm-name">{activeCommunityName}</span>
          </div>
        )}
        <nav className="rail-nav">
          <Link
            to="/"
            className={`rlink${isHome ? ' on' : ''}`}
            aria-current={isHome ? 'page' : undefined}
          >
            <RailIcon name="home" />
            Home
          </Link>
          <Link
            to="/reflections"
            className={`rlink${isReflections ? ' on' : ''}`}
            aria-current={isReflections ? 'page' : undefined}
          >
            <RailIcon name="spark" />
            Reflections
          </Link>
          <Link
            to="/settings/notifications"
            className={`rlink${isSettings ? ' on' : ''}`}
            aria-current={isSettings ? 'page' : undefined}
          >
            <RailIcon name="gear" />
            Settings
          </Link>
          {me?.user.isModerator && (
            <Link
              to="/moderation"
              className={`rlink${isModeration ? ' on' : ''}`}
              aria-current={isModeration ? 'page' : undefined}
            >
              <RailIcon name="shield" />
              Moderation
            </Link>
          )}
          {me?.user.isFounder && (
            <Link
              to="/founder"
              className={`rlink${isFounder ? ' on' : ''}`}
              aria-current={isFounder ? 'page' : undefined}
            >
              <RailIcon name="spark" />
              Founder
            </Link>
          )}
        </nav>
        <div className="rail-foot">
          <ThemeToggle />
          <div className="rail-care">
            Not a crisis service. In an emergency, call or text <strong>988</strong>.
          </div>
          <div className="rail-foot-links">
            <a href="/safety">Safety</a>
            <a href="/guidelines">Guidelines</a>
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
            <button type="button" className="appfoot-link" onClick={() => setFeedbackOpen(true)}>
              Report a bug
            </button>
          </div>
          <button type="button" className="rail-signout" onClick={() => void handleSignOut()}>
            Sign out
          </button>
        </div>
      </aside>

      <div className="app-body">
        {/* Top appbar — web mobile only; the rail replaces it on desktop (CSS).
            Slim like the native shell: wordmark + theme. Primary nav lives in
            the bottom tab bar below; sign-out lives on the Settings page —
            the same posture as the app. */}
        <header className="appbar">
          <Link to="/" className="wordmark">
            <em className="wordmark-accent">Less</em>Alone
          </Link>
          <nav>
            <ThemeToggle />
          </nav>
        </header>
        <Outlet />
        {/* Quiet legal/safety footer — web mobile only (the rail carries these
            on desktop). Anchor tags because /privacy, /terms, /safety,
            /guidelines render outside the BrowserRouter. */}
        <footer className="appfoot" aria-label="About">
          <a href="/safety">Safety</a>
          <a href="/guidelines">Guidelines</a>
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
          <button type="button" className="appfoot-link" onClick={() => setFeedbackOpen(true)}>
            Report a bug
          </button>
          <span className="appfoot-tag">
            Not a crisis service. In an emergency, call or text 988.
          </span>
        </footer>
        {/* Phone-browser shell mirrors the app: the same bottom tab bar the
            Capacitor shell uses. CSS hides it at ≥60rem (the rail takes over)
            and inside a conversation (chat owns the screen). */}
        <NativeTabBar />
      </div>

      {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} />}
    </div>
  );
}
